<template>
  <div class="tw:pt-4">
    <div class="page-header inner tw:py-0">
      <h1>Client Management</h1>

      <ViewFilter
        class="view-filter filter"
        :filters="filters"
        :applied-filters="appliedFilters"
        @change="handleFilterChange" />

      <label class="search-bar tw:input tw:input-sm tw:flex tw:min-w-96 tw:items-center tw:gap-2 tw:place-self-end tw:self-center">
          <input
            ref="searchInputRef"
            type="text"
            placeholder="Search"
            class="tw:grow"
            v-model="searchValue"
            @keyup="handleSearchKeyUp" />
          <XIcon
            v-if="this.searchValue.length > 0"
            :size="20"
            @click="searchValue = ''" />
          <SearchIcon :size="20" />
        </label>
    </div>
    <div class="tw:flex tw:justify-end tw:pt-2 tw:pr-4 tw:gap-4">
      <label
        for="toggle-current-only"
        class="tw:label tw:flex tw:items-center tw:justify-end">
        <input
          id="toggle-current-only"
          type="checkbox"
          v-model="showCurrentOnly"
          :checked="showCurrentOnly"
          class="tw:toggle tw:border tw:border-solid tw:border-gray-400 tw:toggle-md tw:toggle-secondary" />

        <span class="tw:cursor-pointer tw:select-none"> Show Current Only </span>
      </label>
      <button
        class="tw:btn tw:btn-sm tw:border tw:border-gray-300"
        @click="downloadCSV"
        title="Download CSV">
        <Download class="tw:h-4 tw:w-4 tw:stroke-primary" />
      </button>
    </div>

    <div class="inner tw:pt-0 tw:w-full">
      <div
        v-if="!loading && error"
        class="tw:alert tw:alert-error tw:border-t">
        {{ error }}
      </div>
      <div v-if="loading && !error" class="tw:w-full tw:min-w-0 tw:space-y-3">
        <div
          v-for="i in 8"
          :key="i"
          class="tw:flex tw:w-full tw:min-w-0 tw:items-center tw:gap-4 tw:py-2 tw:px-2 tw:rounded-md"
        >
          <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:gap-2">
            <Skeleton shape="rectangle" height="1rem" width="100%" />
            <Skeleton shape="rectangle" height="0.75rem" width="100%" />
          </div>
        </div>
      </div>
      <div v-if="page.accounts && page.accounts.length > 0">
        <table
          class="apptable sortable dense --tight-first mt-2"
          v-if="!loading && !error">
          <thead :style="{ top: `${this.stickyTop}px` }">
            <tr>
              <td class="nosort tw:rounded-tl-md tw:rounded-bl-md">&nbsp;</td>
              <td
                :class="sortClassName('accountName')"
                @click="sort('accountName')">
                Name
              </td>
              <td class="nosort">CAC Entity</td>
              <td class="nosort">Coverages</td>
              <td
                :class="sortClassName('inForcePremium')"
                class="tw:text-right"
                @click="sort('inForcePremium')">
                In-Force Premium
              </td>
              <td
                :class="sortClassName('revenue')"
                class="tw:text-right"
                @click="sort('revenue')">
                Revenue
              </td>
              <td
                v-if="!isExternalClient"
                :class="sortClassName('topLevelIndustry')"
                @click="sort('topLevelIndustry')">
                Industry
              </td>
              <td
                :class="sortClassName('parentAccountName')"
                @click="sort('parentAccountName')">
                Parent Company
              </td>
              <td
                :class="sortClassName('insertedDate')"
                @click="sort('insertedDate')">
                Client Since
              </td>
              <td class="actions tw:rounded-tr-md tw:rounded-br-md">&nbsp;</td>
            </tr>
          </thead>

          <tbody>
            <tr
              v-for="account in page.accounts"
              :key="account.accountID"
              data-test="row">
              <td>
                <div class="tw:flex">
                  <FavoriteIndicator
                    tooltip-position="right"
                    size="sm"
                    v-model="account.favorite"
                    @update:model-value="toggleFavorite(account)"
                    :loading="account.progress" />
                </div>
              </td>
              <td>
                <div class="tw:flex">
                  <div :class="account.hasExternalUsers ? 'tw:w-4/5' : 'tw:w-full'">
                    <div class="name-wrapper">
                      <router-link
                        class="name tw:text-primary"
                        :to="`/account/${account.accountID}/overview`"
                        data-test="account-name">
                        <div v-html="highlight(account.displayName || account.accountName, searchValue)" />
                        <div
                          v-if="account.displayName"
                          v-html="highlight(account.accountName, searchValue)"
                          class="tw:ml-2 tw:text-xs tw:font-light tw:italic" />
                      </router-link>
                    </div>
                    <div class="tw:flex tw:gap-1">
                      <Badge
                        v-if="account.accountStatus === 'P'"
                        class="tw:border-sky-500/80 tw:bg-none tw:font-normal tw:text-sky-500/90"
                        variant="outline">
                        PROSPECT
                      </Badge>
                      <div
                        class="stock tw:flex tw:items-center tw:gap-1.5"
                        v-if="isPublic(account)">
                        <Badge
                          class="tw:border-gray-300 tw:bg-none tw:text-xs tw:font-normal"
                          variant="outline">
                          PUBLIC
                        </Badge>

                        <Badge
                          v-if="account.stockTicker"
                          class="tw:flex tw:shrink-0 tw:items-center tw:gap-1 tw:border-gray-300 tw:whitespace-nowrap"
                          variant="outline">
                          <div
                            class="stock-exchange tw:flex tw:items-center tw:font-normal tw:uppercase"
                            data-test="stock-exchange">
                            {{ account.stockExchange }}
                          </div>
                          <div
                            v-if="account.stockExchange && account.stockTicker"
                            class="tw:flex tw:items-center">
                            :
                          </div>
                          <div
                            class="stock-ticker tw:flex tw:items-center tw:uppercase"
                            data-test="stock-ticker">
                            {{ account.stockTicker }}
                          </div>
                          <div
                            v-if="account.fortuneRank !== undefined && account.fortuneRank !== null"
                            class="tw:tooltip tw:tooltip-right tw:flex tw:items-center"
                            :data-tip="`Fortune 500 Ranking`">
                            <span
                              class="tw:text-[11px] tw:font-semibold tw:text-amber-700"
                              data-test="fortune-rank">
                              #{{ account.fortuneRank }}
                            </span>
                          </div>
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="account.hasExternalUsers"
                    class="tw:flex tw:w-1/5 tw:items-center">
                    <div
                      class="tw:tooltip tw:tooltip-top tw:z-50 tw:mx-auto"
                      data-tip="Account has external users with access to it.">
                      <Users
                        :size="16"
                        class="tw:text-gray-600" />
                    </div>
                  </div>
                </div>
              </td>
              <td>
                <div
                  class="chips tw:m-0 tw:flex tw:flex-wrap tw:gap-1"
                  v-if="account.agencies.length > 0 && account.agencies[0] !== ''">
                  <Badge
                    v-for="agency in account.agencies"
                    :key="agency"
                    variant="outline"
                    class="tw:bg-none tw:font-normal"
                    :class="badgeStyleForAgency(agency)">
                    <div v-html="highlight(agency, searchValue)"></div>
                  </Badge>
                </div>
              </td>
              <td>
                <div
                  class="chips tw:m-0 tw:flex tw:flex-wrap tw:gap-1"
                  v-if="account.coverageTypes.length > 0">
                  <Badge
                    v-for="{ key, coverageType, isExternal } in account.coverageTypes"
                    :key="key"
                    variant="outline"
                    class="tw:border-gray-300 tw:bg-none tw:font-normal tw:text-primary"
                    :class="{ '': isExternal }">
                    <div v-html="highlight(coverageType, searchValue)"></div>
                  </Badge>
                </div>
              </td>
              <td>
                <div
                  v-if="account.inForcePremium > 0"
                  data-test="inforce-premium"
                  class="tw:text-right">
                  {{ formatCurrency(account.inForcePremium) }}
                </div>
                <div
                  class="empty tw:text-right"
                  v-else>
                  &mdash;
                </div>
              </td>
              <td>
                <div
                  v-if="account.revenue > 0"
                  data-test="revenue"
                  class="tw:text-right">
                  {{ formatCurrency(account.revenue) }}
                </div>
                <div
                  class="empty tw:text-right"
                  v-else>
                  &mdash;
                </div>
              </td>
              <td v-if="!isExternalClient">
                <div
                  class="industry"
                  v-if="account.industryName">
                  <div
                    class="industry-top-level"
                    data-test="top-level-industry">
                    <div v-html="highlight(account.industryName, searchValue)"></div>
                  </div>
                </div>
                <div
                  class="empty"
                  v-else>
                  &mdash;
                </div>
              </td>
              <td>
                <router-link
                  :to="`/parent/account/${account.parentCompanyID}/overview`"
                  v-if="account.parentCompany"
                  data-test="parent-company">
                  <div v-html="highlight(account.parentCompany, searchValue)"></div>
                </router-link>
                <div
                  class="empty"
                  v-else>
                  &mdash;
                </div>
              </td>
              <td>
                <div
                  v-if="account.insertedDate"
                  data-test="client-since">
                  {{ formatDate(account.insertedDate) }}
                </div>
                <div
                  class="empty"
                  v-else>
                  &mdash;
                </div>
              </td>
              <td class="actions">
                <router-link
                  :to="`/account/${account.accountID}/edit`"
                  data-test="edit-link"
                  >Edit</router-link
                >
              </td>
            </tr>
          </tbody>
        </table>

        <div
          class="paging-controls tw:pt-4"
          v-if="!loading && !error">
          <div class="paging-buttons tw:flex tw:gap-4">
            <AppButton
              secondary
              :disabled="!page.prevPage"
              @click="currentPage--"
              data-test="prev-button">
              <CircleChevronLeft
                :size="20"
                class="tw:mr-2 tw:text-primary"
              />
              Previous
            </AppButton>
            <AppButton
              secondary
              :disabled="!page.nextPage"
              @click="currentPage++"
              data-test="next-button">
              Next
              <CircleChevronRight
                :size="20"
                class="tw:ml-2 tw:text-primary"
              />
            </AppButton>
          </div>
        </div>
      </div>
      <div v-else-if="!loading && !error">
        <EmptyDisplay
          :src="emptySrc"
          alt="No results found"
          title="No results found" />
      </div>
    </div>
  </div>
</template>

<script>
import { accountService, userAccountService, referenceService } from '@/services/api';
import { FailedToLoadPage } from '@/utilities/Errors';
import { DownloadCSV, TransformAccountsToCSV } from '@/utilities/Download';
import FavoriteIndicator from '@/components/FavoriteIndicator.vue';
import emptySrc from '@/assets/images/empty.svg';
import EmptyDisplay from '@/components/EmptyDisplay.vue';
import { Store } from '@/store';
import { chipColor, agencyChipColors, colors } from '@/utilities/Color';
import { filters } from '@/utilities/Filters';
import Skeleton from '@/components/v2/Skeleton.vue';
import { CircleChevronLeft, CircleChevronRight, Download, SearchIcon, Users, XIcon } from 'lucide-vue-next';
import { IsUserAClient } from '@/utilities/AuthUtils';
import AppButton from '@/components/v2/AppButton.vue';
import Badge from '@/components/ui/Badge.vue';
import { useHeaderOffset } from '@/composables/useHeaderOffset';
import ViewFilter from '@/components/ViewFilter.vue';
import { ref } from 'vue';
import { formatDate } from '@/utilities/Format';
import { formatCurrency } from '@/utilities/Money';

export default {
  name: 'AccountList',
  components: { AppButton, FavoriteIndicator, EmptyDisplay, Skeleton, Users, SearchIcon, XIcon, Badge, ViewFilter, Download, CircleChevronLeft, CircleChevronRight },
  setup() {
    const { totalOffset } = useHeaderOffset();
    const filters = ref([
      {
        category: 'Account Status',
        values: [
          {
            label: 'Clients',
            value: 'I',
          },
          {
            label: 'Prospects',
            value: 'P',
          },
        ],
      },
      {
        category: 'Agencies',
        values: [
          {
            label: 'Specialty',
            value: 'Specialty',
          },
          {
            label: 'Agency',
            value: 'Agency',
          },
          {
            label: 'Capital',
            value: 'Capital',
          },
        ],
      },
      {
        category: 'Ownership Type',
        values: [
          {
            label: 'Public',
            value: 'Public',
          },
          {
            label: 'Private',
            value: 'Private',
          },
          {
            label: 'Not Specified',
            value: 'Not Specified',
          },
        ],
      },
    ]);

    // default applied filters
    const appliedFilters = ref([]);

    return { totalOffset, filters, appliedFilters };
  },
  data: () => ({
    list: [],
    filteredList: [],
    loading: true,
    error: undefined,
    currentPage: 0,
    pageSize: 15,
    searchValue: '',
    accountStatusArray: ['I', 'P'],
    agencyArray: ['Specialty', 'Agency', 'Capital'],
    emptySrc,
    sortField: '',
    sortDirection: '',
    searchTimerID: undefined,
    stickyTop: 0,
    chipColor,
    agencyChipColors,
    colors,
    showCurrentOnly: false,
  }),
  computed: {
    selectedAccountStatuses() {
      return this.appliedFilters?.find((f) => f.category === 'Account Status')?.values.map((v) => v.value) ?? [];
    },
    selectedAgencies() {
      return this.appliedFilters?.find((f) => f.category === 'Agencies')?.values.map((v) => v.value) ?? [];
    },
    selectedSICIndustries() {
      return this.appliedFilters?.find((f) => f.category === 'SIC Industry')?.values.map((v) => v.value) ?? [];
    },
    selectedIndustries() {
      return this.appliedFilters?.find((f) => f.category === 'Industry')?.values.map((v) => v.value) ?? [];
    },
    selectedCoverageTypes() {
      return this.appliedFilters?.find((f) => f.category === 'Coverage Type')?.values.map((v) => v.value) ?? [];
    },
    selectedOwnershipTypes() {
      return this.appliedFilters?.find((f) => f.category === 'Ownership Type')?.values.map((v) => v.value) ?? [];
    },
    selectedRevenueRange() {
      return this.appliedFilters?.find((f) => f.category === 'Revenue')?.range ?? [];
    },
    revenueMin() {
      return this.selectedRevenueRange && this.selectedRevenueRange.length > 0 && this.selectedRevenueRange[0] !== undefined
        ? Number(this.selectedRevenueRange[0])
        : undefined;
    },
    revenueMax() {
      return this.selectedRevenueRange && this.selectedRevenueRange.length > 1 && this.selectedRevenueRange[1] !== undefined
        ? Number(this.selectedRevenueRange[1])
        : undefined;
    },
    selectedFortuneRankRange() {
      return this.appliedFilters?.find((f) => f.category === 'Fortune Rank')?.range ?? [];
    },
    fortuneRankMin() {
      return this.selectedFortuneRankRange && this.selectedFortuneRankRange.length > 0 && this.selectedFortuneRankRange[0] !== undefined
        ? Number(this.selectedFortuneRankRange[0])
        : undefined;
    },
    fortuneRankMax() {
      return this.selectedFortuneRankRange && this.selectedFortuneRankRange.length > 1 && this.selectedFortuneRankRange[1] !== undefined
        ? Number(this.selectedFortuneRankRange[1])
        : undefined;
    },
    isExternalClient() {
      return IsUserAClient(Store.state.user);
    },
    page() {
      return {
        prevPage: this.currentPage > 0,
        nextPage: this.filteredList.length > this.pageSize,
        accounts: this.filteredList.length > this.pageSize ? this.filteredList.slice(0, this.filteredList.length - 1) : this.filteredList,
      };
    },
  },
  mounted() {
    this.registerResizeEvent();
    this.updateStickyTop();
    if (this.$refs.searchInputRef) {
      this.$refs.searchInputRef.focus();
    }
  },
  beforeUnmount() {
    this.unregisterResizeEvent();
  },
  methods: {
    setSICIndustries(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'SIC Industry');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'SIC Industry');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setIndustries(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Industries');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Industries');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setAccountStatuses(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Account Status');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Account Status');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setAgencies(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Agencies');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Agencies');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setOwnershipTypes(values) {
      if (!values) {
        return;
      }
      const allValues = this.filters.find((f) => f.category === 'Ownership Type');
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Ownership Type');
      if (filterGroup) {
        filterGroup.values = values.map((v) => allValues.values.find((av) => av.value === v));
      }
    },
    setRevenueRange(range) {
      if (!range || range.length < 2) return;
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Revenue');
      if (filterGroup) {
        filterGroup.range = [Number(range[0]), Number(range[1])];
      } else {
        // If Revenue filter group not present yet, create it with provided range
        this.appliedFilters.push({ category: 'Revenue', values: [], range: [Number(range[0]), Number(range[1])] });
      }
    },
    setFortuneRankRange(range) {
      if (!range || range.length < 2) return;
      const filterGroup = this.appliedFilters.find((f) => f.category === 'Fortune Rank');
      if (filterGroup) {
        filterGroup.range = [Number(range[0]), Number(range[1])];
      } else {
        // If Fortune Rank filter group not present yet, create it with provided range
        this.appliedFilters.push({ category: 'Fortune Rank', values: [], range: [Number(range[0]), Number(range[1])] });
      }
    },
    handleFilterChange(filters) {
      this.appliedFilters = [...filters];
      this.filterAccounts();
    },
    handleSearchKeyUp(e) {
      if (e.key && e.key === 'Escape') {
        this.searchValue = '';
        e.stopPropagation();
      }
    },
    highlight: filters.highlight,
    formatDate,
    formatCurrency(value) {
      return formatCurrency(Math.round(value), true);
    },
    updateStickyTop() {
      this.stickyTop = this.totalOffset + 4;
    },
    registerResizeEvent() {
      window.addEventListener('resize', this.updateStickyTop);
    },
    unregisterResizeEvent() {
      window.removeEventListener('resize', this.updateStickyTop);
    },
    async toggleFavorite(account) {
      const updateListProgress = (list, accountID, progress) => list.map((a) => (a.accountID === accountID ? { ...a, progress } : a));
      const updateListFavorite = (list, accountID, favorite) => list.map((a) => (a.accountID === accountID ? { ...a, favorite } : a));

      this.filteredList = updateListProgress(this.filteredList, account.accountID, true);
      if (!account.favorite) {
        await userAccountService.remove(account.accountID);
        this.filteredList = updateListFavorite(this.filteredList, account.accountID, false);
      } else {
        await userAccountService.create(account.accountID);
        this.filteredList = updateListFavorite(this.filteredList, account.accountID, true);
      }
      this.filteredList = updateListProgress(this.filteredList, account.accountID, false);
    },
    isPublic(account) {
      return account.ownershipType?.toLowerCase() === 'public';
    },
    sort(key) {
      if (this.sortField !== key) {
        this.sortField = key;
        this.sortDirection = 'ascending';
      } else {
        switch (this.sortDirection) {
          case 'ascending':
            this.sortDirection = 'descending';
            break;
          case 'descending':
            this.sortField = '';
            this.sortDirection = '';
            break;
          default:
            this.sortDirection = 'ascending';
        }
      }
      this.filterAccounts();
    },
    badgeStyleForAgency(agency) {
      switch (agency.trim()) {
        case 'Specialty':
          return 'tw:text-specialty-teal/90 tw:border-specialty-teal/80';
        case 'Agency':
          return 'tw:text-agency-red/90 tw:border-agency-red/80';
        case 'Capital':
          return 'tw:text-capital-blue/90 tw:border-capital-blue/80';
        default:
          return '';
      }
    },
    search(value) {
      if (this.loading) {
        return;
      }
      this.searchValue = value;
      if (this.searchTimerID) {
        clearTimeout(this.searchTimerID);
      }
      this.searchTimerID = setTimeout(() => {
        this.currentPage = 0;
        this.filterAccounts();
      }, 300);
    },
    sortClassName(key) {
      if (this.sortField !== key) {
        return null;
      }
      if (this.sortDirection) {
        return { [this.sortDirection]: true, sorted: true };
      }
      return null;
    },
    async downloadCSV() {
      const list = await accountService.search({
        search: this.searchValue,
        agencies: this.selectedAgencies,
        accountStatuses: this.selectedAccountStatuses,
        sicCodes: this.selectedSICIndustries,
        industryIds: this.selectedIndustries,
        coverageTypeIds: this.selectedCoverageTypes,
        ownershipType: this.selectedOwnershipTypes,
        limit: 999_999_999,
        offset: 0,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        showCurrentOnly: this.showCurrentOnly,
        revenueMin: this.revenueMin,
        revenueMax: this.revenueMax,
        fortuneRankMin: this.fortuneRankMin,
        fortuneRankMax: this.fortuneRankMax,
      });

      DownloadCSV(TransformAccountsToCSV(list, this.isExternalClient), 'Accounts');
    },
    async filterAccounts() {
      try {
        this.loading = true;
        this.filteredList = await this.searchAccounts();
      } catch (err) {
        console.error(err);
        this.error = FailedToLoadPage;
      } finally {
        this.loading = false;
      }
    },
    setSearchCriteriaFromQuery() {
      const { a, q, t, p, s, d, si, i, rmin, rmax, ot, frmin, frmax } = this.$route.query;
      if (a || q || t || p || s || d || si || i || rmin || rmax || ot || frmin || frmax) {
        this.setAccountStatuses(t ? [...t] : []);
        this.setAgencies(a ? [...a] : []);
        this.setSICIndustries(si ? [...si] : []);
        this.setIndustries(i ? [...i] : []);
        this.setOwnershipTypes(ot ? [...ot] : []);
        if (rmin !== undefined || rmax !== undefined) {
          const min = rmin !== undefined ? Number(rmin) : 0;
          const max = rmax !== undefined ? Number(rmax) : 0;
          this.setRevenueRange([min, max]);
        }
        if (frmin !== undefined || frmax !== undefined) {
          const min = frmin !== undefined ? Number(frmin) : 1;
          const max = frmax !== undefined ? Number(frmax) : 1000;
          this.setFortuneRankRange([min, max]);
        }
        this.searchValue = q || '';
        this.currentPage = (p && typeof p === 'string' && !isNaN(parseInt(p)) && parseInt(p)) || 0;
        this.sortField = s || '';
        this.sortDirection = d || '';
      }
    },
    searchCriteriaAsQuery() {
      const query = {};
      if (this.searchValue) query.q = this.searchValue;
      if (this.selectedAccountStatuses.length > 0) query.t = this.selectedAccountStatuses;
      if (this.selectedAgencies.length > 0) query.a = this.selectedAgencies;
      if (this.selectedSICIndustries.length > 0) query.si = this.selectedSICIndustries;
      if (this.selectedIndustries.length > 0) query.i = this.selectedIndustries;
      if (this.selectedCoverageTypes.length > 0) query.ct = this.selectedCoverageTypes;
      if (this.selectedOwnershipTypes.length > 0) query.ot = this.selectedOwnershipTypes;
      if (this.revenueMin !== undefined) query.rmin = this.revenueMin;
      if (this.revenueMax !== undefined) query.rmax = this.revenueMax;
      if (this.fortuneRankMin !== undefined) query.frmin = this.fortuneRankMin;
      if (this.fortuneRankMax !== undefined) query.frmax = this.fortuneRankMax;
      if (this.currentPage > 0) query.p = `${this.currentPage}`;
      if (this.sortField) query.s = this.sortField;
      if (this.sortDirection) query.d = this.sortDirection;
      return query;
    },
    async searchAccounts() {
      const query = this.searchCriteriaAsQuery();

      if (JSON.stringify(this.$route.query) !== JSON.stringify(query)) {
        await this.$router.replace({ query }).catch(() => {
          return '';
        });
      }

      const list = await accountService.search({
        search: this.searchValue,
        agencies: this.selectedAgencies,
        accountStatuses: this.selectedAccountStatuses,
        sicCodes: this.selectedSICIndustries,
        industryIds: this.selectedIndustries,
        coverageTypeIds: this.selectedCoverageTypes,
        ownershipType: this.selectedOwnershipTypes,
        limit: this.pageSize + 1,
        offset: this.currentPage * this.pageSize,
        sortField: this.sortField,
        sortDirection: this.sortDirection,
        showCurrentOnly: this.showCurrentOnly,
        revenueMin: this.revenueMin,
        revenueMax: this.revenueMax,
        fortuneRankMin: this.fortuneRankMin,
        fortuneRankMax: this.fortuneRankMax,
      });

      return list.map((account) => {
        // coverages
        const { coverageTypes: coverageTypeStr } = account;
        const coverageTypeDataList = coverageTypeStr.split(',').filter((t) => t !== '');
        const coverageTypes = coverageTypeDataList
          .map((coverageData) => {
            if (coverageData.includes('||')) {
              const [coverageType, isExternal] = coverageData.split('||');

              return { key: coverageData, coverageType, isExternal: isExternal.toLowerCase() === 'true' };
            } else {
              return { key: coverageData, coverageType: coverageData, isExternal: false };
            }
          })
          .sort((a, b) => (a.isExternal === b.isExternal ? 0 : a.isExternal ? 1 : -1));

        // agencies
        const agencies = account.agencies?.split(',');
        return { ...account, coverageTypes, coverageTypeStr, agencies };
      });
    },
    async refresh() {
      this.loading = true;
      this.currentPage = 0;

      try {
        /*
          A `q` param is added to the url if the user searches, sorts, or pages.
          If the user arrives back here by some other means besides clicking the
          back button (e.g. clicking the header), then the state will be reset
          and not restored.
        */
        this.setSearchCriteriaFromQuery();

        this.filteredList = await this.searchAccounts();
      } catch (err) {
        console.error(err);
        this.error = FailedToLoadPage;
      } finally {
        this.loading = false;
      }
    },
  },
  async created() {
    await this.refresh();
    const [coverageTypes, clientIndustries, industries] = await Promise.all([
      referenceService.coverageTypes(),
      referenceService.sicCodes(),
      referenceService.industries(),
    ]);
    const maxRevenue = Math.max(0, ...(this.filteredList || []).map((a) => Number(a.revenue) || 0));
    this.filters = [
      ...this.filters,
      { category: 'Coverage Type', values: coverageTypes },
      { category: 'SIC Industry', values: clientIndustries.map((i) => ({ label: i.name, value: i.sicCode })) },
      { category: 'Industry', values: industries.map((i) => ({ label: i.name, value: i.ID })) },
      { category: 'Revenue', description: 'Filter accounts by revenue', values: [], range: [0, maxRevenue], prefix: '$' },
      { category: 'Fortune Rank', description: 'Filter accounts by Fortune Ranking', values: [], range: [1, 1000], prefix: '#' },
    ];
  },
  watch: {
    currentPage() {
      window.scrollTo(0, 0);
      this.filterAccounts();
    },
    searchValue() {
      this.search(this.searchValue);
    },
    showCurrentOnly() {
      this.filterAccounts();
    },
    totalOffset: {
      handler() {
        this.updateStickyTop();
      },
      immediate: true,
    },
  },
};
</script>

<style scoped>
.page-header {
  display: grid;
  grid-template-columns: auto auto 1fr;
  grid-template-areas: 'header filter search-bar';
  grid-column-gap: 1rem;
  align-items: center;
  align-content: center;
}

.page-header > h1 {
  grid-area: header;
}

.page-header > .view-filter {
  grid-area: filter;
}
.page-header > .button-group.agencies {
  padding: 0.5rem 0;
}

@media (max-width: 1200px) {
  .page-header {
    grid-template-columns: auto auto 1fr;
    grid-template-areas: 'header header search-bar' 'filter filter filter';
  }

  .page-header > .button-group.account-status,
  .page-header > .button-group.agencies {
    padding: 0.5rem 0;
  }

  .page-header > .button-group.client-types {
    width: fit-content;
    justify-self: start;
    text-transform: uppercase;
  }

  .page-header > .search-bar {
    max-width: unset;
    justify-self: unset;
  }
}

@media (max-width: 600px) {
  .page-header {
    grid-template-columns: 1fr;
    grid-template-areas: 'header' 'filter' 'search-bar';
  }

  .page-header > .button-group.account-status {
    padding: 1rem 0 0;
  }

  .page-header > .button-group.agencies {
    padding: 1rem 0;
  }

  .page-header > .search-bar {
    width: 100%;
  }
}

.name-wrapper {
  max-width: 25em;
}

.name {
  font-weight: 500;
  letter-spacing: 0.02em;
  font-size: 1.1em;
}

.stock {
  display: grid;
  grid-template-columns: auto auto auto auto 1fr;
  grid-column-gap: 0.25rem;
  font-size: 0.9em;
  align-items: center;
  align-content: center;
}

.stock-ticker {
  font-weight: 600;
  color: var(--v-gray-darken1);
  letter-spacing: 0.02em;
}

.stock-exchange {
  color: var(--v-gray-base);
}

.industry-description {
  color: var(--v-gray-base);
}

.stock-public {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7em !important;
}

.prospect {
  font-weight: 700;
  text-transform: uppercase;
  font-size: 0.7em !important;
}

.empty {
  color: var(--v-gray-lighten3);
}

.chips {
  margin-top: 1em;
  max-width: 20vw;
}

thead {
  position: sticky;
  z-index: 2;
}
</style>
